// src/components/VerifyEmail.js - Email Verification Landing Page
import React, { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { verifyEmailToken } from '../services/emailService';
import './VerifyEmail.css';

const VerifyEmail = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const [status, setStatus] = useState('verifying'); // 'verifying', 'success', 'error', 'invalid'
  const [message, setMessage] = useState('');
  const [email, setEmail] = useState('');
  const [countdown, setCountdown] = useState(5);

  // Extract token and email from URL parameters
  const token = searchParams.get('token');
  const emailParam = searchParams.get('email');

  useEffect(() => {
    const verifyEmail = async () => {
      console.log('🔐 Starting email verification...', { token, emailParam });
      
      // Validate URL parameters
      if (!token || !emailParam) {
        setStatus('invalid');
        setMessage('Ongeldige verificatielink. Controleer of je de volledige link uit je email hebt geklikt.');
        return;
      }

      setEmail(emailParam);

      try {
        // Call verification service
        const result = await verifyEmailToken(token, emailParam);
        
        if (result.success) {
          setStatus('success');
          setMessage(result.message || 'Je email is succesvol geverifieerd!');
          
          // Start countdown for auto-redirect
          const timer = setInterval(() => {
            setCountdown(prev => {
              if (prev <= 1) {
                clearInterval(timer);
                navigate('/login', { 
                  replace: true, 
                  state: { 
                    verified: true, 
                    email: emailParam,
                    message: 'Email succesvol geverifieerd! Je kunt nu inloggen.' 
                  }
                });
                return 0;
              }
              return prev - 1;
            });
          }, 1000);

          return () => clearInterval(timer);
        } else {
          setStatus('error');
          setMessage(result.error || 'Er is een fout opgetreden bij het verifiëren van je email.');
        }
      } catch (error) {
        console.error('❌ Verification error:', error);
        setStatus('error');
        setMessage('Er is een technische fout opgetreden. Probeer het later opnieuw.');
      }
    };

    verifyEmail();
  }, [token, emailParam, navigate]);

  const handleRetryVerification = () => {
    setStatus('verifying');
    setMessage('');
    window.location.reload();
  };

  const renderContent = () => {
    switch (status) {
      case 'verifying':
        return (
          <div className="verify-content">
            <div className="verify-icon loading">
              <div className="spinner"></div>
            </div>
            <h2 className="verify-title">Email Verifiëren...</h2>
            <p className="verify-description">
              We verifiëren je email adres. Dit duurt een paar seconden.
            </p>
          </div>
        );

      case 'success':
        return (
          <div className="verify-content success">
            <div className="verify-icon success">
              ✅
            </div>
            <h2 className="verify-title">Email Geverifieerd!</h2>
            <p className="verify-description">
              Je email adres <strong>{email}</strong> is succesvol geverifieerd.
            </p>
            
            {countdown > 0 && (
              <div className="countdown-notice">
                <p>Je wordt automatisch doorgestuurd naar de inlogpagina over {countdown} seconden...</p>
              </div>
            )}

            <div className="verify-actions">
              <Link to="/login" className="btn-primary">
                Nu Inloggen
              </Link>
            </div>
          </div>
        );

      case 'error':
        return (
          <div className="verify-content error">
            <div className="verify-icon error">
              ❌
            </div>
            <h2 className="verify-title">Verificatie Mislukt</h2>
            <p className="verify-description">
              {message}
            </p>
            
            <div className="verify-actions">
              <button onClick={handleRetryVerification} className="btn-primary">
                Opnieuw Proberen
              </button>
              <Link to="/register" className="btn-secondary">
                Nieuw Account Aanmaken
              </Link>
            </div>
          </div>
        );

      case 'invalid':
        return (
          <div className="verify-content invalid">
            <div className="verify-icon error">
              ⚠️
            </div>
            <h2 className="verify-title">Ongeldige Link</h2>
            <p className="verify-description">
              {message}
            </p>
            
            <div className="help-section">
              <h3>Wat kun je doen?</h3>
              <ul>
                <li>Controleer of je de volledige link uit je email hebt geklikt</li>
                <li>Check je spam/junk folder voor de verificatie email</li>
                <li>Maak een nieuw account aan als de link is verlopen</li>
              </ul>
            </div>
            
            <div className="verify-actions">
              <Link to="/register" className="btn-primary">
                Nieuw Account Aanmaken
              </Link>
              <Link to="/login" className="btn-secondary">
                Probeer Inloggen
              </Link>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="verify-container">
      <div className="verify-card">
        <div className="verify-header">
          <h1>RelatieScan</h1>
          <p>Email Verificatie</p>
        </div>
        
        {renderContent()}
        
        <div className="verify-footer">
          <Link to="/" className="footer-link">
            ← Terug naar Homepage
          </Link>
        </div>
      </div>
    </div>
  );
};

export default VerifyEmail;